import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { useCallback, useReducer, useRef, useMemo } from 'react';
import { getNewUTCDate } from "../../../utils/time";
import { FEATURES } from "../../../lib/services/copilot";
import { getAgents } from "../../../lib/services/copilotAgents";
import { useWeb5Context } from "../../../lib/Web5Context";
import { errorToast } from "../../../utils/toast";

/**
 * Default properties for a chat interaction
 * @property {string} content - The chat message content
 * @property {string} role - The role of the interaction ('user' or 'assistant')
 * @property {string} vote - Vote status for the interaction
 * @property {boolean} isLoading - Whether the interaction is currently loading
 * @property {boolean} isFinished - Whether the interaction is complete
 * @property {boolean} isError - Whether an error occurred during the interaction
 * @property {string|null} parentId - ID of the parent interaction if this is a response
 * @property {Array} actions - Array of action objects associated with the interaction
 * @property {boolean} disableVote - Whether voting is disabled for this interaction
 */
const defaultChat = {
  content: '',
  role: '',
  vote: '',
  isLoading: false,
  isFinished: false,
  isError: false,
  parentId: null,
  actions: [],
  disableVote: false
};

/**
 * Default properties for a single chat session
 * @property {Object} chat - Map of chat interactions indexed by interaction ID
 * @property {Object} chatSession - Session data for the chat
 * @property {boolean} isInitialized - Whether the chat session has been initialized
 * @property {Object|null} activeAgent - The currently active copilot agent for this chat
 * @property {Object} reports - Reports data associated with the chat session
 * @property {Object} tables - Table data associated with the chat session
 */
const defaultChatState = {
  chat: {},
  chatSession: {},
  isInitialized: false,
  activeAgent: null,
  reports: {},
  tables: {}
};

/**
 * Initial state for the copilot reducer
 * @property {Object} chats - Map of chat sessions indexed by chatId
 * @property {Array} copilotAgents - List of available copilot agents
 * @property {Object} goldenMinute - Golden minute feature state with CTA view tracking
 */
const initialState = {
  chats: {},
  copilotAgents: [],
  goldenMinute: {
    ctaViews: 0
  }
};

/**
 * Action types for the copilot reducer
 * @property {string} SET_INTERACTION - Update or create a chat interaction
 * @property {string} CLEAR_CHAT - Clear all chat interactions for a specific chatId
 * @property {string} CLEAR_ALL_CHATS - Clear all chat interactions
 * @property {string} SET_CHAT_SESSION - Set the current chat session for a specific chatId
 * @property {string} SET_INITIALIZED - Set initialization status for a specific chatId
 * @property {string} SET_COPILOT_AGENTS - Set available copilot agents
 * @property {string} SET_ACTIVE_AGENT - Set the active copilot agent for a specific chatId
 * @property {string} SET_VOTE - Set the vote for a specific interaction
 * @property {string} DANA_GOLDEN_MINUTE_CTA_NUM_VIEWS - Increment the golden minute CTA views counter
 */
const ACTIONS = {
  SET_INTERACTION: 'SET_INTERACTION',
  CLEAR_CHAT: 'CLEAR_CHAT',
  CLEAR_ALL_CHATS: 'CLEAR_ALL_CHATS',
  SET_CHAT_SESSION: 'SET_CHAT_SESSION',
  SET_INITIALIZED: 'SET_INITIALIZED',
  SET_COPILOT_AGENTS: 'SET_COPILOT_AGENTS',
  SET_ACTIVE_AGENT: 'SET_ACTIVE_AGENT',
  SET_VOTE: 'SET_VOTE',
  DANA_GOLDEN_MINUTE_CTA_NUM_VIEWS: 'DANA_GOLDEN_MINUTE_CTA_NUM_VIEWS'
};

/**
 * Reducer function for managing copilot state
 * @param {Object} state - Current state of the copilot
 * @param {Object} action - Action object with type and payload
 * @param {string} action.type - The type of action to perform
 * @param {Object} action.payload - The data payload for the action
 * @returns {Object} Updated state after applying the action
 */
const copilotReducer = (state, action) => {
  switch (action.type) {
    case ACTIONS.SET_INTERACTION:
      {
        const {
          chatId,
          id,
          text,
          responseId,
          isFinished,
          isQuestion,
          isLoading,
          isError,
          parentId,
          action: actionProp,
          table: tableProp,
          report: reportProp,
          creationDate,
          disableVote
        } = action.payload;
        if (!id || !chatId) return state;

        // Initialize chat if it doesn't exist
        const currentChat = state.chats[chatId] || _objectSpread({}, defaultChatState);
        const interaction = currentChat.chat[id] || _objectSpread({
          creationDate: creationDate || getNewUTCDate()
        }, defaultChat);
        const updatedInteraction = _objectSpread({}, interaction);
        if (isError) {
          updatedInteraction.isError = true;
          updatedInteraction.isFinished = true;
          updatedInteraction.isLoading = false;
        } else {
          if (text) updatedInteraction.content = text;
          if (typeof isLoading === 'boolean') updatedInteraction.isLoading = isLoading;
          if (isFinished) updatedInteraction.isFinished = isFinished;
          if (responseId) updatedInteraction.responseId = responseId;
          if (parentId) updatedInteraction.parentId = parentId;
          if (typeof disableVote === 'boolean') updatedInteraction.disableVote = disableVote;
          if (actionProp) updatedInteraction.actions = [...updatedInteraction.actions, actionProp];
        }
        updatedInteraction.role = isQuestion ? 'user' : 'assistant';

        // Update tables and reports at the chat level
        const updatedChatState = _objectSpread({}, currentChat);
        if (tableProp) {
          updatedChatState.tables = _objectSpread(_objectSpread({}, currentChat.tables), tableProp);
        }
        if (reportProp) {
          updatedChatState.reports = _objectSpread(_objectSpread({}, currentChat.reports), reportProp);
        }
        const newState = _objectSpread(_objectSpread({}, state), {}, {
          chats: _objectSpread(_objectSpread({}, state.chats), {}, {
            [chatId]: _objectSpread(_objectSpread({}, updatedChatState), {}, {
              chat: _objectSpread(_objectSpread({}, currentChat.chat), {}, {
                [id]: updatedInteraction
              })
            })
          })
        });
        return newState;
      }
    case ACTIONS.CLEAR_CHAT:
      {
        const {
          chatId
        } = action.payload;
        if (!chatId) return state;
        return _objectSpread(_objectSpread({}, state), {}, {
          chats: _objectSpread(_objectSpread({}, state.chats), {}, {
            [chatId]: _objectSpread({}, defaultChatState)
          })
        });
      }
    case ACTIONS.CLEAR_ALL_CHATS:
      return _objectSpread(_objectSpread({}, state), {}, {
        chats: {}
      });
    case ACTIONS.SET_CHAT_SESSION:
      {
        const {
          chatId,
          session
        } = action.payload;
        if (!chatId) return state;
        const currentChat = state.chats[chatId] || _objectSpread({}, defaultChatState);
        return _objectSpread(_objectSpread({}, state), {}, {
          chats: _objectSpread(_objectSpread({}, state.chats), {}, {
            [chatId]: _objectSpread(_objectSpread({}, currentChat), {}, {
              chatSession: session
            })
          })
        });
      }
    case ACTIONS.SET_INITIALIZED:
      {
        const {
          chatId,
          value
        } = action.payload;
        if (!chatId) return state;
        const currentChat = state.chats[chatId] || _objectSpread({}, defaultChatState);
        return _objectSpread(_objectSpread({}, state), {}, {
          chats: _objectSpread(_objectSpread({}, state.chats), {}, {
            [chatId]: _objectSpread(_objectSpread({}, currentChat), {}, {
              isInitialized: value
            })
          })
        });
      }
    case ACTIONS.SET_COPILOT_AGENTS:
      return _objectSpread(_objectSpread({}, state), {}, {
        copilotAgents: action.payload
      });
    case ACTIONS.SET_ACTIVE_AGENT:
      {
        const {
          chatId,
          agent
        } = action.payload;
        if (!chatId) return state;
        const currentChat = state.chats[chatId] || _objectSpread({}, defaultChatState);
        return _objectSpread(_objectSpread({}, state), {}, {
          chats: _objectSpread(_objectSpread({}, state.chats), {}, {
            [chatId]: _objectSpread(_objectSpread({}, currentChat), {}, {
              activeAgent: agent
            })
          })
        });
      }
    case ACTIONS.SET_VOTE:
      {
        const {
          chatId,
          interactionId,
          vote
        } = action.payload;
        if (!chatId || !interactionId) return state;
        const currentChat = state.chats[chatId] || _objectSpread({}, defaultChatState);
        const interaction = currentChat.chat[interactionId];
        if (!interaction) return state;
        return _objectSpread(_objectSpread({}, state), {}, {
          chats: _objectSpread(_objectSpread({}, state.chats), {}, {
            [chatId]: _objectSpread(_objectSpread({}, currentChat), {}, {
              chat: _objectSpread(_objectSpread({}, currentChat.chat), {}, {
                [interactionId]: _objectSpread(_objectSpread({}, interaction), {}, {
                  vote: vote
                })
              })
            })
          })
        });
      }
    case ACTIONS.DANA_GOLDEN_MINUTE_CTA_NUM_VIEWS:
      {
        var _state$goldenMinute;
        return _objectSpread(_objectSpread({}, state), {}, {
          goldenMinute: {
            ctaViews: ((_state$goldenMinute = state.goldenMinute) === null || _state$goldenMinute === void 0 ? void 0 : _state$goldenMinute.ctaViews) + 1
          }
        });
      }
    default:
      return state;
  }
};

/**
 * Custom hook for managing copilot chat interactions, agent selection, and streaming operations
 *
 * This hook provides a comprehensive interface for managing copilot functionality including:
 * - Chat interaction management (create, update, clear) for multiple concurrent chats
 * - Agent selection and management per chatId
 * - Chat session handling per chatId
 * - Stream reader management for real-time responses
 * - Initialization state tracking per chatId
 *
 * @returns {Object} Chat management functions and state
 * @property {Object} chats - Map of chat sessions indexed by chatId
 * @property {Array} copilotAgents - List of available copilot agents with their configurations
 * @property {Object|null} defaultAgent - The default copilot agent (SAGE_COPILOT) from the available agents
 * @property {Function} setInteraction - Update or create a chat interaction with specified chatId and parameters
 * @property {Function} clearChat - Clear all chat interactions for a specific chatId
 * @property {Function} clearAllChats - Clear all chat interactions for all chats
 * @property {Function} setChatSession - Set the current chat session for a specific chatId
 * @property {Function} setIsInitialized - Set initialization status for a specific chatId
 * @property {Function} setActiveAgent - Set the active copilot agent for a specific chatId
 * @property {Function} setCopilotAgents - Set the complete list of available copilot agents
 * @property {Function} refreshAgents - Fetch and refresh the agents list from the API, updating state (currently disabled)
 * @property {Function} getStreamReader - Get the current active stream reader for a specific chatId
 * @property {Function} setStreamReader - Set the active stream reader for a specific chatId
 * @property {Function} getChatState - Get the chat state for a specific chatId (includes tables and reports at chat level)
 * @property {Function} setVote - Set the vote for a specific interaction
 * @property {number} goldenMinuteCtaViews - Number of times the golden minute CTA has been viewed
 * @property {Function} updateGoldenMinuteCtaViews - Increment the golden minute CTA views counter
 */
const useCopilot = () => {
  const {
    t
  } = useWeb5Context();
  const [state, dispatch] = useReducer(copilotReducer, initialState);
  const streamReadersRef = useRef({}); // Track active stream readers per chatId

  /**
   * Updates or creates a chat interaction
   * @param {Object} params - Interaction parameters
   * @param {string} params.chatId - Unique identifier for the chat session (required)
   * @param {string} [params.text] - Chat message content
   * @param {string} params.id - Unique identifier for the interaction (required)
   * @param {string} [params.responseId] - ID of the response
   * @param {boolean} [params.isFinished] - Whether the interaction is complete
   * @param {boolean} [params.isQuestion] - Whether this is a user question (determines role)
   * @param {boolean} [params.isLoading] - Whether the interaction is loading
   * @param {boolean} [params.isError] - Whether an error occurred
   * @param {string} [params.parentId] - ID of the parent interaction
   * @param {Object} [params.action] - Action object to be added to the interaction's actions array
   * @param {Date|string} [params.creationDate] - Override the creation date for the interaction
   * @param {boolean} [params.disableVote] - Whether to disable voting for this interaction
   * @param {Object} [params.table] - Table data to be merged into the chat session's tables
   * @param {Object} [params.report] - Report data to be merged into the chat session's reports
   */
  const setInteraction = useCallback(function (_temp) {
    let {
      chatId,
      text,
      id,
      responseId,
      isFinished,
      isQuestion,
      isLoading,
      isError,
      parentId,
      action,
      creationDate,
      disableVote,
      table,
      report
    } = _temp === void 0 ? {} : _temp;
    dispatch({
      type: ACTIONS.SET_INTERACTION,
      payload: {
        chatId,
        text,
        id,
        responseId,
        isFinished,
        isQuestion,
        isLoading,
        isError,
        parentId,
        action,
        creationDate,
        disableVote,
        table,
        report
      }
    });
  }, []);

  /**
   * Clears all chat interactions and resets the chat state for a specific chatId
   * Also cancels any ongoing stream reader for the specified chat
   * @param {Object} params - Parameters object
   * @param {string} params.chatId - Unique identifier for the chat session (required)
   * @param {boolean} [params.skipCancelStreamReader=false] - Whether to skip canceling the stream reader for this chat
   */
  const clearChat = useCallback(function (_temp2) {
    let {
      chatId,
      skipCancelStreamReader = false
    } = _temp2 === void 0 ? {} : _temp2;
    // Cancel and cleanup stream reader for this chat
    const reader = streamReadersRef.current[chatId];
    if (reader && !skipCancelStreamReader) {
      try {
        reader.cancel();
      } catch (error) {
        console.warn('Error canceling stream reader for chatId:', chatId, error);
      }
      delete streamReadersRef.current[chatId];
    }
    dispatch({
      type: ACTIONS.CLEAR_CHAT,
      payload: {
        chatId
      }
    });
  }, []);

  /**
   * Clears all chat interactions for all chats
   * Also cancels all ongoing stream readers
   */
  const clearAllChats = useCallback(() => {
    // Cancel and cleanup all stream readers
    Object.entries(streamReadersRef.current).forEach(_ref => {
      let [chatId, reader] = _ref;
      if (reader) {
        try {
          reader.cancel();
        } catch (error) {
          console.warn('Error canceling stream reader for chatId:', chatId, error);
        }
      }
    });
    streamReadersRef.current = {};
    dispatch({
      type: ACTIONS.CLEAR_ALL_CHATS
    });
  }, []);

  /**
   * Sets the currently active copilot agent for a specific chatId
   * @param {Object} params - Parameters object
   * @param {string} params.chatId - Unique identifier for the chat session (required)
   * @param {Object|null} params.agent - The agent object to set as active, or null to clear
   */
  const setActiveAgent = useCallback(function (_temp3) {
    let {
      chatId,
      agent
    } = _temp3 === void 0 ? {} : _temp3;
    dispatch({
      type: ACTIONS.SET_ACTIVE_AGENT,
      payload: {
        chatId,
        agent
      }
    });
  }, []);

  /**
   * Updates the list of available copilot agents
   * @param {Array} agents - Array of agent objects (required)
   */
  const setCopilotAgents = useCallback(agents => {
    dispatch({
      type: ACTIONS.SET_COPILOT_AGENTS,
      payload: agents
    });
  }, []);

  /**
   * Sets the chat session for a specific chatId
   * @param {Object} params - Parameters object
   * @param {string} params.chatId - Unique identifier for the chat session (required)
   * @param {Object} params.session - Session data to set
   */
  const setChatSession = useCallback(function (_temp4) {
    let {
      chatId,
      session
    } = _temp4 === void 0 ? {} : _temp4;
    dispatch({
      type: ACTIONS.SET_CHAT_SESSION,
      payload: {
        chatId,
        session
      }
    });
  }, []);

  /**
   * Sets the initialization status for a specific chatId
   * @param {Object} params - Parameters object
   * @param {string} params.chatId - Unique identifier for the chat session (required)
   * @param {boolean} params.value - Initialization status
   */
  const setIsInitialized = useCallback(function (_temp5) {
    let {
      chatId,
      value
    } = _temp5 === void 0 ? {} : _temp5;
    dispatch({
      type: ACTIONS.SET_INITIALIZED,
      payload: {
        chatId,
        value
      }
    });
  }, []);

  /**
   * Sets the vote for a specific interaction
   * @param {Object} params - Parameters object
   * @param {string} params.chatId - Unique identifier for the chat session (required)
   * @param {string} params.interactionId - Unique identifier for the interaction (required)
   * @param {string} params.vote - Vote value to set
   */
  const setVote = useCallback(function (_temp6) {
    let {
      chatId,
      interactionId,
      vote
    } = _temp6 === void 0 ? {} : _temp6;
    dispatch({
      type: ACTIONS.SET_VOTE,
      payload: {
        chatId,
        interactionId,
        vote
      }
    });
  }, []);

  /**
   * Gets the chat state for a specific chatId
   * @param {Object} params - Parameters object
   * @param {string} params.chatId - Unique identifier for the chat session (required for meaningful operation)
   * @returns {Object} Chat state containing chat, chatSession, isInitialized and activeAgent
   */
  const getChatState = useCallback(function (_temp7) {
    let {
      chatId
    } = _temp7 === void 0 ? {} : _temp7;
    return state.chats[chatId] || _objectSpread({}, defaultChatState);
  }, [state.chats]);

  /**
   * Sets the active stream reader for managing streaming responses for a specific chatId
   * @param {Object} params - Parameters object
   * @param {string} params.chatId - Unique identifier for the chat session (required)
   * @param {ReadableStreamDefaultReader|null} params.reader - The stream reader to set as active
   */
  const setStreamReader = useCallback(function (_temp8) {
    let {
      chatId,
      reader
    } = _temp8 === void 0 ? {} : _temp8;
    if (!chatId) return;
    if (reader) {
      streamReadersRef.current[chatId] = reader;
    } else {
      delete streamReadersRef.current[chatId];
    }
  }, []);

  /**
   * Gets the current active stream reader for a specific chatId
   * @param {Object} params - Parameters object
   * @param {string} params.chatId - Unique identifier for the chat session (required for meaningful operation)
   * @returns {ReadableStreamDefaultReader|null} The current active stream reader for the chat, or null if none is set
   */
  const getStreamReader = useCallback(function (_temp9) {
    let {
      chatId
    } = _temp9 === void 0 ? {} : _temp9;
    if (!chatId) return null;
    return streamReadersRef.current[chatId] || null;
  }, []);
  const getDefaultAgent = useCallback(newAgents => {
    return (newAgents || state.copilotAgents).find(agent => agent.name === FEATURES.SAGE_COPILOT);
  }, [state.copilotAgents]);
  const defaultAgent = useMemo(() => {
    return getDefaultAgent();
  }, [getDefaultAgent]);

  /**
   * Fetches and updates the agents list
   * Updates active agents for all existing chats if their current agent is no longer available
   * @note Currently disabled - returns early without performing any operations (TODO: Remove when API is ready)
   * @returns {Promise<Array>|undefined} Promise that resolves to the agents data array when enabled, undefined when disabled
   * @throws {Error} When the API call fails or encounters an error (only when enabled)
   */
  const handleGetAgents = useCallback(() => {
    return getAgents().then(_ref2 => {
      let {
        data
      } = _ref2;
      dispatch({
        type: ACTIONS.SET_COPILOT_AGENTS,
        payload: data
      });
      const defaultAgent = getDefaultAgent(data);

      // Update active agents for all existing chats
      Object.keys(state.chats).forEach(chatId => {
        const chatState = state.chats[chatId];
        const currentAgent = chatState.activeAgent;

        // If chat doesn't have an agent or current agent is no longer available
        if (!currentAgent || !data.find(agent => agent.name === (currentAgent === null || currentAgent === void 0 ? void 0 : currentAgent.name))) {
          // Set default agent for this chat
          if (defaultAgent) {
            setActiveAgent({
              chatId,
              agent: defaultAgent
            });
          }
        }
      });
      return data;
    }).catch(error => {
      console.error(error);
      errorToast({
        t,
        title: t('cfm_label_error_generic')
      });
      throw error;
    });
  }, [getDefaultAgent, state.chats, setActiveAgent, t]);

  /**
   * Increments the golden minute CTA views counter
   * Used to track how many times the golden minute call-to-action has been viewed
   */
  const updateGoldenMinuteCtaViews = useCallback(() => {
    dispatch({
      type: ACTIONS.DANA_GOLDEN_MINUTE_CTA_NUM_VIEWS
    });
  }, []);
  return {
    chats: state.chats,
    clearAllChats,
    clearChat,
    copilotAgents: state.copilotAgents,
    defaultAgent,
    getChatState,
    getStreamReader,
    refreshAgents: handleGetAgents,
    setActiveAgent,
    setChatSession,
    setCopilotAgents,
    setInteraction,
    setIsInitialized,
    setStreamReader,
    setVote,
    goldenMinuteCtaViews: state.goldenMinute.ctaViews,
    updateGoldenMinuteCtaViews
  };
};
export default useCopilot;